import { Modal } from "@mantine/core";
import { useMemo } from "react";

// Flexible modal component that can be used to display any content in a modal
// with a variety of positions and sizes

export enum ModalPosition {
  Center = "center",
  TopLeft = "top-left",
  TopCenter = "top-center",
  TopRight = "top-right",
  BottomLeft = "bottom-left",
  BottomCenter = "bottom-center",
  BottomRight = "bottom-right",
  LeftCenter = "left-center",
  RightCenter = "right-center",
}

export enum ModalSizes {
  ExtraSmall = "xs",
  Small = "sm",
  Medium = "md",
  Large = "lg",
  XL = "xl",
}
interface TableModalWrapperProps {
  opened: boolean;
  onClose?: () => void;
  onSubmit?: () => void;
  withCloseButton?: boolean;
  withOverlay?: boolean;
  children: React.ReactNode;
  position?: ModalPosition;
  size?: ModalSizes;
  offset?: { x?: number; y?: number }; // Offset from edges
}

export const TableModalWrapper = ({
  opened,
  onClose,
  onSubmit,
  withCloseButton = true,
  position = ModalPosition.Center,
  size = ModalSizes.Medium,
  withOverlay = true,
  children,
  offset = { x: 20, y: 20 },
}: TableModalWrapperProps) => {
  const innerStyles = useMemo(() => {
    const baseInnerStyles: React.CSSProperties = {
      display: "flex",
      padding: 0,
      alignItems: "flex-start",
      justifyContent: "flex-start",
    };

    switch (position) {
      case ModalPosition.TopCenter:
        return {};

      case ModalPosition.TopLeft:
        return {
          ...baseInnerStyles,
          alignItems: "flex-start",
          justifyContent: "flex-start",
          paddingTop: 0,
          paddingLeft: offset.x,
        };

      case ModalPosition.TopRight:
        return {
          ...baseInnerStyles,
          alignItems: "flex-start",
          justifyContent: "flex-end",
          paddingTop: 0,
          paddingRight: offset.x,
        };

      case ModalPosition.BottomLeft:
        return {
          ...baseInnerStyles,
          alignItems: "flex-end",
          justifyContent: "flex-start",
          paddingBottom: 0,
          paddingLeft: offset.x,
        };

      case ModalPosition.BottomCenter:
        return {
          ...baseInnerStyles,
          alignItems: "flex-end",
          justifyContent: "center",
          paddingBottom: 0,
        };

      case ModalPosition.BottomRight:
        return {
          ...baseInnerStyles,
          alignItems: "flex-end",
          justifyContent: "flex-end",
          paddingBottom: 0,
          paddingRight: offset.x,
        };

      case ModalPosition.LeftCenter:
        return {
          ...baseInnerStyles,
          alignItems: "center",
          justifyContent: "flex-start",
          paddingLeft: offset.x,
        };

      case ModalPosition.RightCenter:
        return {
          ...baseInnerStyles,
          alignItems: "center",
          justifyContent: "flex-end",
          paddingRight: offset.x,
        };

      default:
        return baseInnerStyles;
    }
  }, [position, offset]);

  return (
    <Modal
      opened={opened}
      onClose={onClose || (() => {})}
      onSubmit={onSubmit}
      withCloseButton={withCloseButton}
      size={size}
      withOverlay={withOverlay}
      centered={position === ModalPosition.Center}
      styles={{ inner: innerStyles }}
      transitionProps={
        position.includes("bottom")
          ? { duration: 150, transition: "slide-up" }
          : position.includes("top")
            ? { duration: 150, transition: "slide-down" }
            : { duration: 150, transition: "fade" }
      }
      lockScroll={false}
      shadow="xl"
    >
      {children}
    </Modal>
  );
};
